import React, { useMemo, useState } from "react";
import styled from "styled-components";

import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import AddIcon from "@mui/icons-material/Add";

import get from "lodash/get";

import RelatedDocuments from "./RelatedDocuments";
import Button from "../shared/Button";

import EstateItemHeader from "./EstateItemHeader";
import NewDocumentForm from "./NewDocumentForm";
import UploadDocuments from "./UploadDocuments";
import DraftsList from "./DraftsList";

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          className="rounded-box"
          sx={{ backgroundColor: "var(--light-grey-bg)", p: "30px" }}
        >
          {children}
        </Box>
      )}
    </div>
  );
};

const EstateDetail = ({ estate, openSelectUnitsModal }) => {
  const [tabIndex, setTabIndex] = useState(0);

  const {
    id,
    name,
    address,
    city,
    country,
    energyClass,
    contacts,
    canHaveChildren,
    parentId,
  } = estate;
  const isProject = useMemo(() => canHaveChildren && !parentId, [estate]);

  const fullAddress = [address, city, get(country, "name")]
    .filter(Boolean)
    .join(", ");

  const handleChange = (_event, newValue) => {
    setTabIndex(newValue);
  };

  const chevronRight = <img src="/assets/icons/chevron-right.svg" alt="" />;

  return (
    <StyleWrapper>
      <div className="estate-detail-header rounded-box d-flex justify-content-between">
        <EstateItemHeader
          id={id}
          name={name}
          fullAddress={fullAddress}
          energyClass={energyClass}
        />
      </div>
      <div className="estate-detail-content d-flex">
        <div className="d-flex flex-column">
          <Tabs
            orientation="vertical"
            variant="scrollable"
            value={tabIndex}
            onChange={handleChange}
            className="estate-detail-tabs"
            TabIndicatorProps={{
              hidden: true,
            }}
          >
            <Tab
              label="Documents liés"
              iconPosition="end"
              disableRipple
              icon={tabIndex === 0 && chevronRight}
            />
            <Tab
              label="Documents créés"
              iconPosition="end"
              disableRipple
              icon={tabIndex === 1 && chevronRight}
            />
            <Tab
              label="Brouillons"
              iconPosition="end"
              disableRipple
              icon={tabIndex === 2 && chevronRight}
            />
          </Tabs>
          <div className="action-btn-container d-flex flex-column gap-2 mt-auto">
            <Button
              color="primary"
              variant="contained"
              icon={<AddIcon />}
              onClick={() => handleChange(null, 4)}
            >
              Upload documents
            </Button>
            <Button
              color="success"
              variant="contained"
              icon={<AddIcon />}
              onClick={() => handleChange(null, 3)}
            >
              Créer un nouveau document
            </Button>
          </div>
        </div>
        <div className="estate-detail-content__tab-content">
          <TabPanel value={tabIndex} index={0}>
            <RelatedDocuments
              estateId={id}
              documentTypes={[]}
              withNoInformation={true}
              isCreated={false}
            />
          </TabPanel>
          <TabPanel value={tabIndex} index={1}>
            <RelatedDocuments
              title="Liste des documents créés"
              estateId={id}
              isCreated={true}
            />
          </TabPanel>
          <TabPanel value={tabIndex} index={2}>
            <DraftsList estateId={id} />
          </TabPanel>
          <TabPanel value={tabIndex} index={3}>
            <NewDocumentForm
              estate={estate}
              contacts={contacts}
              isProject={isProject}
              openSelectUnitsModal={openSelectUnitsModal}
            />
          </TabPanel>
          <TabPanel value={tabIndex} index={4}>
            <UploadDocuments estateId={id} />
          </TabPanel>
        </div>
      </div>
    </StyleWrapper>
  );
};

const StyleWrapper = styled.div`
  .estate-detail-header {
    min-width: 500px;
    background-color: var(--light-grey-bg);
    flex-grow: 1;
    padding: 20px 15px 20px 30px;
  }

  .download-btn {
    width: 53px;
    height: 53px;
    background-color: var(--menu-active-color);
  }

  .estate-detail-content {
    margin-top: 20px;
    gap: 30px;
  }

  .estate-detail-content__tab-content {
    width: 1200px;
    padding-bottom: 25px;
  }

  .estate-detail-tabs {
    margin-left: 32px;

    button {
      width: 260px;
      height: 55px;
      min-height: unset;
      padding: 0;
      font-size: 15px;
      color: var(--text-color);
      text-transform: capitalize;
      justify-content: space-between;
      border-bottom: 0.5px solid var(--menu-active-color);

      &.Mui-selected {
        font-weight: 700;
        text-decoration: underline;
      }
    }
  }
  .action-btn-container {
    margin-bottom: 25px;
    margin-left: 32px;

    button {
      font-size: 13px;
      height: 48px;
    }
  }
`;

export default EstateDetail;
